import moment from 'moment';

import { getHotelBookingSystem } from "./WorkspaceReservation/workspaceReservationUtil";
import { isNetworkError } from '../../../../../util/networkUtil';
import { stringFormatter } from '../../../../../util/formatUtil';
import { getZulu } from '../../../Events/dateUtil';
import { escSqlQuote } from '../../../../../aiim/util/selectionUtil';
import { findFieldName, getAttributeValue } from '../../../../../aiim/util/aiimUtil';

import { ModalController }  from "../../../../../../src/common/components/Modal/index";
import { cancelBooking, updateCheckInOut } from "./WorkspaceReservation/OfficeHotelingInterface";

import Context from '../../../../../context/Context';
import Topic from '../../../../../context/Topic';
import FieldNames from '../../../../../aiim/datasets/FieldNames';
import { getRecurrenceSeries, IBookingData, IBookingsState } from './MyBookings';
import { IApplyEditsResult } from '@esri/arcgis-rest-feature-layer';

const APPROVED = 1;
const CHECKED_IN = 4;

export async function filterHotelReservations (bookings: __esri.Graphic[], hasRecurrenceFields: boolean){
  if (!bookings || !bookings.length) {
    return [];
  }

  const unitsDataset = Context.getInstance().aiim.datasets.units;
  const unitFields = unitsDataset.layer2D && unitsDataset.layer2D.fields;
  const hotelBookings: IBookingData[] = [];

  // Gather the unit IDs from the reservations to make a query
  const unitIds = bookings
    .map((booking) => {
      const bookingAttrs = booking.attributes;
      const unitId = getAttributeValue(bookingAttrs, FieldNames.UNIT_ID);
      return `'${escSqlQuote(unitId)}'`;
    });
  // To make sure we don't add duplicate ids to the query
  // Ex: UNIT_ID IN ('1', '1', '2', '2') -> UNIT_ID IN ('1', '2')
  const uniqueIds = [...new Set(unitIds)];

  const assignmentTypeField = findFieldName(unitFields, FieldNames.UNITS_SPACE_ASSIGNMENT_TYPE);
  const whereClauses = [
    `${unitsDataset.uidField} IN (${uniqueIds.join(", ")})`,
    `${assignmentTypeField} = 'hotel'`
  ];
  const where = `${whereClauses.join(" AND ")}`;

  try {
    const results = await unitsDataset.query(where);
    const units = (results && results.features) || [];

    // Filter units by those that are hotels
    const hotelingUnitIds = units.map((unit) => getAttributeValue(unit.attributes, FieldNames.UNIT_ID));

    // Find the bookings that have hoteling unit IDs
    bookings.forEach((booking) => {
      const bookingAttrs = booking.attributes;
      const unitId = getAttributeValue(bookingAttrs, FieldNames.UNIT_ID);
      if (hotelingUnitIds.includes(unitId)) {
        const unit = units.find((unit) => getAttributeValue(unit.attributes, FieldNames.UNIT_ID) === unitId);
        const hotelBooking: IBookingData = { booking, unit };
        if (hasRecurrenceFields) {
          const id = getAttributeValue(bookingAttrs, FieldNames.RECURRENCE_ID);
          const config = getAttributeValue(bookingAttrs, FieldNames.RECURRENCE_CONFIG);
          if (id && config?.length > 0) {
            hotelBooking.recurrenceId = id;
          }
        }
        hotelBookings.push(hotelBooking);
      }
    });
  } catch(e) {
    console.error(e);
  }
  return hotelBookings;
}

export async function getUserReservations(bookingType, bookingDateFilter): Promise<IBookingsState> {
    const reservations = Context.getInstance().aiim.datasets 
                          && Context.getInstance().aiim.datasets.reservations;
    if (!reservations) return ({isWorking: false});

    const username = Context.getInstance().user.getUsername();
    const { reservedForUsernameField, reservedByUsernameField, endTimeField, stateField  } = reservations;

    if (!username || !reservedForUsernameField) return ({isWorking: false});

    try {
      const now = getZulu(new Date());
      let whereClauses;
      if (bookingType === "otherbookings") {
        whereClauses = [
          `${reservedByUsernameField} = '${escSqlQuote(username)}'`,
          `${reservedForUsernameField} <> '${escSqlQuote(username)}'`,
          `(${endTimeField} > TIMESTAMP '${escSqlQuote(now)}' OR ${stateField} = ${CHECKED_IN})`
        ];
      } else {
        whereClauses = [
          `${reservedForUsernameField} = '${escSqlQuote(username)}'`,
          `(${endTimeField} > TIMESTAMP '${escSqlQuote(now)}' OR ${stateField} = ${CHECKED_IN})`
        ];
      }
      if (bookingDateFilter && bookingDateFilter.where) {
        whereClauses.push(bookingDateFilter.where);
      }
      const myReservations = await reservations.query(whereClauses.join(" AND "));
      const bookings = (myReservations && myReservations.features) || [];
      const hotelBookings = await filterHotelReservations(bookings, reservations.hasRecurrenceFields());
      const series = getRecurrenceSeries(hotelBookings);
      
      return ({ bookings: hotelBookings, isWorking: false, series })

    } catch(e) {
      console.error("Unable to query for user reservations", e);
      if (e && isNetworkError(e.message)) {
        const i18n = Context.getInstance().i18n;
        Topic.publishNetworkError(i18n.meetingRooms.issues.m7);
      } else {
        Topic.publishErrorAccessingData();
      }
      return ({bookings: [], isWorking: false})
    }
}

export function showServerErrorPopup(type, attributes){
    const unitName = getAttributeValue(attributes, FieldNames.UNIT_NAME);
    const reservations = Context.instance.aiim.datasets.reservations;
    const bookingSystem = getHotelBookingSystem();
    const i18n = Context.getInstance().i18n;
    let message;

    if(type === "checkIn") {
      message = i18n.more.bookWorkspace.checkInOut.checkIn.serverErrorMessage;
    } else if (type === "checkOut") {
      message = i18n.more.bookWorkspace.checkInOut.checkOut.serverErrorMessage;
    } else if (type === "cancel") {
      message = i18n.more.bookWorkspace.checkInOut.cancelBooking.serverErrorMessage;
    }
    message = message.replace("{unit}", unitName);

    const options = {
      title: i18n.more.bookWorkspace.checkInOut.error,
      message: message,
      okLabel: i18n.more.bookWorkspace.checkInOut.tryAgain,
      showOkCancel: true,
      hideCancel : false,
      closeOnOK: true,
      cancelLabel: i18n.more.bookWorkspace.checkInOut.close
    };

    ModalController.confirm(options)
    .then(result => {
      if (result.ok) {
        let objectIdField;
        if(reservations && reservations.layer2D && reservations.layer2D.objectIdField) {
          objectIdField = reservations.layer2D.objectIdField;
        }
        const objectID = getAttributeValue(attributes, objectIdField);
        if (type === "cancel") {
          cancelBooking(bookingSystem, attributes).then(({ data }: { data: IApplyEditsResult }) => {
            if (data?.updateResults?.some(r => r.success === false)) {
              Topic.publishErrorUpdatingData();
            } else {
              Topic.publish(Topic.ShowToast, { message: i18n.more.bookWorkspace.checkInOut.cancelBooking.toastMessage });
              Topic.publish(Topic.RenderBookingList, {});
              Topic.publish(Topic.UpdateReservationDetails, { objectId: objectID });
            }
          }).catch(error => {
            if (error && isNetworkError(error.message)) {
              showServerErrorPopup(type, attributes);
            } else {
              Topic.publishErrorUpdatingData();
            }
            console.error(error);
          });
        } else {
          if(type === "checkOut") attributes[reservations.checkOutTimeField] = new Date().getTime();
          if(type === "checkIn") attributes[reservations.checkInTimeField] = new Date().getTime();

          updateCheckInOut(bookingSystem, attributes, type)
          .then((res)=> {
            if(res && res.updateFeatureResults && res.updateFeatureResults[0] && 
              res.updateFeatureResults[0].error){
                Topic.publishErrorUpdatingData(res.updateFeatureResults[0].error)
              } else {
                let msg = i18n.more.bookWorkspace.checkInOut.checkOut.toastMessage;
                if(type === "checkIn") msg = i18n.more.bookWorkspace.checkInOut.checkIn.toastMessage;
                Topic.publish(Topic.ShowToast,{
                  message: msg
                });
                Topic.publish(Topic.RenderBookingList, {});
                Topic.publish(Topic.UpdateReservationDetails, {objectId : objectID});
              }
          })
          .catch(error => {
            if(error && isNetworkError(error.message)) {
              showServerErrorPopup(type, attributes);
            } else {
              Topic.publishErrorUpdatingData();
            }
            console.error(error);
          })
        }
      }
    })
    .catch(error => {
      console.error(error);
      Topic.publishErrorUpdatingData()
    })
  }

  export function hideCheckInButton(attributes) {
    const startTime = getAttributeValue(attributes, FieldNames.START_TIME);
    const checkInTime = getAttributeValue(attributes, FieldNames.CHECK_IN_TIME);
    const endTime = getAttributeValue(attributes, FieldNames.END_TIME);
    const state = getAttributeValue(attributes, FieldNames.STATE);
    const currentTime = new Date().getTime();
    const startTimeM = moment(startTime);
    const currentTimeM = moment(currentTime);

    const elapsed = startTimeM.diff(currentTimeM, 'seconds');

    if(!checkInTime) {
      if((state === APPROVED) &&
          (((0 <= elapsed) && (elapsed <= 300)) || ((startTime <= currentTime) && ( currentTime <= endTime)))){
        return false;
      } else return true;
    }else return false;
  }

  export function getRemindersList (sortedBookings, type){
    let reservations = sortedBookings;
    if (!sortedBookings || sortedBookings.length === 0) return;

    let listReminders = {
      checkIn: [],
      checkOut: []
    };
    reservations.forEach((data) => {
      let attributes;
      if(type === "mybookings") attributes = data.booking.attributes;
      else if(type === "otherbookings") attributes = data.reservations[0].attributes;
      const startTime = getAttributeValue(attributes, FieldNames.START_TIME);
      const endTime = getAttributeValue(attributes, FieldNames.END_TIME);
      const state = getAttributeValue(attributes, FieldNames.STATE);

      // Comparisons
      const startTimeM = moment(startTime);
      const currentTime = moment();
      const elapsed = startTimeM.diff(currentTime, 'seconds');
      const needsCheckIn = (0 <= elapsed && elapsed <= 300) 
                            || (startTime <= currentTime && currentTime <= endTime);
      const needsCheckOut = currentTime.isAfter(moment(endTime));

      if (state === APPROVED && needsCheckIn) {
        listReminders.checkIn.push(attributes);
      } else if (state === CHECKED_IN && needsCheckOut) {
        listReminders.checkOut.push(attributes);
      }
    });
    return listReminders;
  }

    /**
   * Show a reminder popup to prompt the user to check in/out
   * 
   * @param {"checkIn" | "checkOut"} type 
   */

  export async function showReminderPopup(type, listReminders, bookingType?) {
    let reminders = listReminders[type];
    if (!reminders || reminders.length === 0) return;

    const i18n = Context.getInstance().i18n;
    let reminderUnitNames = reminders.map((reminder) => getAttributeValue(reminder, FieldNames.UNIT_NAME)).join(", ");
    const template = type === "checkIn" 
    ? i18n.more.bookWorkspace.checkInOut.reminder.newMessage 
    : i18n.more.bookWorkspace.checkInOut.checkOut.reminderMessageAlt;
    const message = stringFormatter(template, { units: reminderUnitNames, hotel: reminderUnitNames });

    const options = {
      title: i18n.more.bookWorkspace.checkInOut.reminder.caption,
      okLabel: i18n.more.bookWorkspace.checkInOut.reminder.close,
      message: message,
      showOkCancel: true,
      hideCancel : true,
      closeOnOK: true
    };

    const refresh = () => {
      Topic.publish(Topic.RenderBookingList, {});
      Topic.publish(Topic.UpdateReservationDetails, {});
    }

    const showToast = (attr) => {
      const person = getAttributeValue(attr, FieldNames.RESERVED_FOR_FULL_NAME);
      let msg = i18n.more.bookWorkspace.checkInOut[type].toastMessage;
      if (bookingType === "otherbookings" && type === "checkIn") {
        msg = i18n.more.bookWorkspace.checkInOut.someoneElse.confirmationSubtitleCheckIn;
        msg = msg.replace("{person}", person);
      }

      Topic.publish(Topic.ShowToast,{
          message: msg
      });
    }

    if (reminders.length === 1) {
      const reservations = Context.instance.aiim.datasets && Context.instance.aiim.datasets.reservations;
      const bookingSystem = getHotelBookingSystem();
      const attributes = reminders[0];

      if (type === "checkIn") {
        attributes[reservations.checkInTimeField] = new Date().getTime();
      } else if (type === "checkOut") {
        attributes[reservations.checkOutTimeField] = new Date().getTime();
      }

      const unitName = attributes[reservations.unitNameField];
      let message;
      let template = type === "checkIn" 
        ? i18n.more.bookWorkspace.checkInOut.checkIn.bookingReady
        : i18n.more.bookWorkspace.checkInOut.checkOut.reminderMessageAlt;
      
      message = stringFormatter(template, { unit: unitName, hotel: unitName });

      let attributesRem;
      if (bookingType === "otherbookings" && type === "checkIn") {
        const checkInItem = listReminders && listReminders.checkIn;
        if (checkInItem && checkInItem.length > 0) attributesRem = checkInItem && checkInItem[0];
        const unitName = getAttributeValue(attributesRem, FieldNames.UNIT_NAME);
        const person = getAttributeValue(attributesRem, FieldNames.RESERVED_FOR_FULL_NAME);
        message = i18n.more.bookWorkspace.checkInOut.someoneElse.checkinReminder;
        message = message.replace("{unit name}", unitName);
        message = message.replace("{person}", person);
      }

      options.message = message;
      options["okLabel"] = i18n.more.bookWorkspace[type];
      options["hideCancel"] = false;
      options["cancelLabel"] = i18n.more.bookWorkspace.checkInOut.notNow;

      try {
        const result = await ModalController.confirm(options);
        if (result && result.ok) {
          const updateResponse = await updateCheckInOut(bookingSystem, attributes, type);
          if (
            updateResponse && 
            updateResponse.updateFeatureResults && 
            updateResponse.updateFeatureResults[0] && 
            updateResponse.updateFeatureResults[0].error
          ) {
            throw new Error(updateResponse.updateFeatureResults[0].error);
          } else {
            showToast(attributesRem);
            refresh();
          }
        }
      } catch (error) {
        console.error(error);
        Topic.publishErrorUpdatingData(error);
      } finally {
        if (type === "checkIn" && listReminders.checkOut.length > 0) {
          await showReminderPopup("checkOut", listReminders["checkOut"]);
        }
      }
    } else {
      try {
        await ModalController.confirm(options);
      } catch (error) {
        console.error(error);
        Topic.publishErrorUpdatingData(error);
      } finally {
        if (type === "checkIn" && listReminders.checkOut.length > 0) {
          await showReminderPopup("checkOut", listReminders["checkOut"]);
        }
      }
    }
  }

export function showNoEditPopup (type, reservationManager){
    const i18n = Context.getInstance().i18n;
    
    let title;
    if (type === "checkIn") title = i18n.more.bookWorkspace.checkInOut.checkIn.caption;
    else if (type === "checkOut") title = i18n.more.bookWorkspace.checkInOut.checkOut.caption;
    else title = i18n.more.bookWorkspace.checkInOut.cancelBooking.caption;

    let message = i18n.more.bookWorkspace.checkInOut.noEditPermissonMsg;
    message = message.replace("{reservationManagerName}", reservationManager);
    const options = {
      title: title,
      message: message,
      okLabel: i18n.more.bookWorkspace.checkInOut.ok,
      closeOnOK: true,
      hideCancel: true
    };
    ModalController.confirm(options);
}
